"use client"

import { useState } from "react"
import { Phone, Shield, Award, Clock } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { whatsappUrl } from "@/lib/contact"

interface QuoteFormProps {
  dict: {
    label: string
    title: string
    description: string
    badges: {
      crea: string
      iso: string
      warranty: string
    }
    form: {
      name_label: string
      name_placeholder: string
      whatsapp_label: string
      whatsapp_placeholder: string
      type_label: string
      type_placeholder: string
      submit: string
      privacy: string
    }
    projectTypes: string[]
    success: {
      title: string
      description: string
      button: string
    }
    whatsapp_message: string
  }
}

export function QuoteForm({ dict }: QuoteFormProps) {
  const [name, setName] = useState("")
  const [whatsapp, setWhatsapp] = useState("")
  const [projectType, setProjectType] = useState("")
  const [submitted, setSubmitted] = useState(false)

  const trustBadges = [
    { icon: Shield, label: dict.badges.crea },
    { icon: Award, label: dict.badges.iso },
    { icon: Clock, label: dict.badges.warranty },
  ]

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!name || !whatsapp || !projectType) return

    const message = dict.whatsapp_message
      .replace("{name}", name)
      .replace("{projectType}", projectType)
      .replace("{whatsapp}", whatsapp)

    window.open(whatsappUrl(message), "_blank")
    setSubmitted(true)
  }

  return (
    <section id="orcamento" className="bg-primary py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="flex flex-col items-center gap-12 lg:flex-row lg:gap-16">
          {/* Left content */}
          <div className="flex-1 text-center lg:text-left">
            <p className="text-xs font-bold uppercase tracking-widest text-primary-foreground/80">
              {dict.label}
            </p>
            <h2 className="mt-3 font-serif text-3xl font-black tracking-tight text-primary-foreground md:text-4xl lg:text-5xl text-balance">
              {dict.title}
            </h2>
            <p className="mt-4 max-w-lg text-lg leading-relaxed text-primary-foreground text-pretty">
              {dict.description}
            </p>

            {/* Trust badges */}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 lg:justify-start">
              {trustBadges.map((badge) => (
                <div
                  key={badge.label}
                  className="flex items-center gap-2 text-primary-foreground"
                >
                  <badge.icon className="h-5 w-5" />
                  <span className="text-xs font-bold uppercase tracking-wider">
                    {badge.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="w-full max-w-md">
            {submitted ? (
              <div className="rounded-xl bg-primary-foreground/10 p-8 text-center backdrop-blur-sm">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-accent/20">
                  <Phone className="h-8 w-8 text-accent" />
                </div>
                <h3 className="text-xl font-bold text-primary-foreground">
                  {dict.success.title}
                </h3>
                <p className="mt-2 text-sm text-primary-foreground/70">
                  {dict.success.description}
                </p>
                <Button
                  onClick={() => setSubmitted(false)}
                  variant="outline"
                  className="mt-6 border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                >
                  {dict.success.button}
                </Button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-4 rounded-xl bg-primary-foreground/10 p-8 backdrop-blur-sm"
              >
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="name"
                    className="text-xs font-bold uppercase tracking-wider text-primary-foreground"
                  >
                    {dict.form.name_label}
                  </label>
                  <Input
                    id="name"
                    placeholder={dict.form.name_placeholder}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="border-primary-foreground/20 bg-primary-foreground/5 text-primary-foreground placeholder:text-primary-foreground/60 focus-visible:ring-accent"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="whatsapp"
                    className="text-xs font-bold uppercase tracking-wider text-primary-foreground"
                  >
                    {dict.form.whatsapp_label}
                  </label>
                  <Input
                    id="whatsapp"
                    placeholder={dict.form.whatsapp_placeholder}
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    required
                    type="tel"
                    className="border-primary-foreground/20 bg-primary-foreground/5 text-primary-foreground placeholder:text-primary-foreground/60 focus-visible:ring-accent"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="project-type"
                    className="text-xs font-bold uppercase tracking-wider text-primary-foreground"
                  >
                    {dict.form.type_label}
                  </label>
                  <Select value={projectType} onValueChange={setProjectType}>
                    <SelectTrigger
                      id="project-type"
                      className="border-primary-foreground/40 bg-primary-foreground/10 text-primary-foreground data-[placeholder]:text-primary-foreground/70 focus:ring-accent"
                    >
                      <SelectValue placeholder={dict.form.type_placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {dict.projectTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="mt-2 bg-accent text-accent-foreground font-bold hover:bg-accent/90 gap-2"
                >
                  <Phone className="h-5 w-5" />
                  {dict.form.submit}
                </Button>

                <p className="text-center text-xs text-primary-foreground/80">
                  {dict.form.privacy}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
